import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  Paper,
  alpha,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import { properCase } from '../../../../helpers/utils';
import {
  KeyboardArrowRight,
  BusinessRounded,
  LocationOnOutlined,
  CalendarTodayOutlined,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getConsultantV2Facilities } from '../../../../helpers/api';

export default function ConsultantFacilities({ consultantUserId }) {
  const history = useHistory();

  const [facilityList, setFacilitList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);

  useEffect(() => {
    fetchFacilityList();
  }, [consultantUserId]);

  const fetchFacilityList = async () => {
    setFacilityLoading(true);
    try {
      const res = await getConsultantV2Facilities(consultantUserId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setFacilitList(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.desc || 'Error fetching facilities';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setFacilityLoading(false);
    }
  };

  const renderContent = () => {
    const columns = [
      {
        name: 'facilityName',
        label: 'Facility Details',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const samplePointCount = facilityList[index]?.samplePointCount;
            const facilityName = facilityList[index]?.facilityName;
            return (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    display: 'flex',
                  }}
                >
                  <BusinessRounded sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {facilityName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    {`${samplePointCount} ${
                      samplePointCount > 1 ? 'Sample Points' : 'Sample Point'
                    }`}
                  </Typography>
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'facilityCategoryName',
        label: 'Category',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const category = facilityList[index]?.facilityCategoryName;
            let themeColor = '#757575';
            if (category === 'SUPER') themeColor = '#d32f2f';
            if (category === 'LARGE') themeColor = '#ed6c02';
            if (category === 'MEDUIM') themeColor = '#0288d1';
            if (category === 'SMALL') themeColor = '#2e7d32';
            if (category === 'MINI') themeColor = '#9c27b0';

            return (
              <Chip
                label={properCase(category)}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: alpha(themeColor, 0.1),
                  color: themeColor,
                  borderRadius: '6px',
                  minWidth: '65px',
                }}
              />
            );
          },
        },
      },
      {
        name: 'stateName',
        label: 'Location',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocationOnOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" fontWeight={500}>
                {facilityList[index]?.stateName}
              </Typography>
            </Stack>
          ),
        },
      },
      {
        name: 'entryDate',
        label: 'Registration',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="body2">
                {new Date(facilityList[index].entryDate).toLocaleDateString('en-NG', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            </Stack>
          ),
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const status = facilityList[index]?.status;
            const isActive = status === 'ACTIVE';
            return (
              <Chip
                label={properCase(status)}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  borderColor: isActive ? 'success.light' : 'warning.light',
                  color: isActive ? 'success.main' : 'warning.main',
                  bgcolor: isActive ? alpha('#2e7d32', 0.05) : alpha('#ed6c02', 0.05),
                }}
              />
            );
          },
        },
      },
      {
        name: '',
        label: 'Action',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
              align="center"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box align="center">
              <Button
                variant="text"
                size="small"
                endIcon={<KeyboardArrowRight />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                onClick={() =>
                  history.push(`../../facilities/${facilityList[index]?.facilityId}/view`)
                }
              >
                Preview
              </Button>
            </Box>
          ),
        },
      },
    ];

    return (
      <MUIDataTable
        data={facilityList}
        columns={columns}
        options={{
          elevation: 0,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({
            size: 'small',
            sx: {
              '& .MUIDataTableToolbar-root': { display: 'none' },
              '& .MuiTableCell-head': { py: 1.5 },
            },
          }),
        }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BusinessRounded color="action" sx={{ mr: 1 }} />
        <Typography variant="h6" color="text.primary" fontWeight={700}>
          Assigned Facilities
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {facilityLoading ? (
          <Box p={3}>
            <LoaderSkeleton />
            <Divider sx={{ my: 2 }} />
            <LoaderSkeleton />
          </Box>
        ) : facilityList?.length < 1 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Empty description="No facilities assigned to this consultant" />
          </Box>
        ) : (
          renderContent()
        )}
      </Paper>
    </Box>
  );
}
